import {Request} from "express";
import {Headers} from "node-fetch";

import {IRequestMeta} from "@pg-mono/request";

import {IABTestConfiguration, IVariantConfigParams, VariantConfig, VariantWeights} from "../types/IABTestConfiguration";
import {buildABTestCookie} from "./ab_testing_utils";
import {EXCLUDED_VARIANT, getAbTestVariantFromQuery} from "./get_ab_test_variant_from_query";
import {getWeightedRandomVariant} from "./get_weighted_random_index";

const WEIGHT_SUM_TOLERANCE = 0.0001;

/**
 * Determine AB test variant from cookies or assign a new random variant.
 * @param abTestConfig {IABTestConfiguration} AB test configuration object
 * @param domain {string} domain value will be set on AB test cookie
 * @param req {Request} request object
 * @param meta {IRequestMeta} request meta object
 * @param params {IVariantConfigParams} parameters for variant config (e.g. isAuthenticated)
 * @returns {string} AB test variant
 */
export const specifyABTestVariant = (
    {id, expires, variants, sampleRate, variantConfig}: IABTestConfiguration,
    domain: string,
    req?: Partial<Request>,
    meta?: Partial<IRequestMeta>,
    params?: IVariantConfigParams
): string => {
    // QA wants to always test base variant in automated tests
    const isE2eTesting = req?.headers?.["x-request-source"] === "e2e";

    const abTestVariantFromCookie = getABTestVariantFromCookies(id, req);

    // Resolve and validate weights based on config and params
    let weights: VariantWeights | undefined;
    if (variantConfig && params) {
        weights = resolveVariantWeights(variantConfig, variants, params);
        validateVariantWeights(weights, variants);
    }

    let abTestVariant = isE2eTesting ? variants[0] : drawABTestVariant(variants, weights);

    if (sampleRate) {
        const isTestGroup = Math.random() < sampleRate;

        if (!isTestGroup) {
            /*
             * "_excluded" indicates that the user is not part of the test group.
             * "_excluded" is identical to the first variant (control) in terms of rendered HTML,
             * but this value should not generate GTM event.
             * We need to store this value in the cookie to avoid group changes on every request.
             */
            abTestVariant = EXCLUDED_VARIANT;
        }
    }

    /*
     * With use of ab_test_experiment and ab_test_variant query params we can override AB test variant.
     * Just add above params to the URL - if valid values will override AB test variant from cookie.
     */
    const abTestVariantFromQuery = getAbTestVariantFromQuery(id, variants, Boolean(sampleRate), req);

    if ((!abTestVariantFromCookie || abTestVariantFromQuery != null) && meta) {
        const headers = new Headers({"set-cookie": buildABTestCookie(id, abTestVariantFromQuery ?? abTestVariant, expires, domain)});

        meta?.serverResponseHeaders?.appendHeaders(headers);
    }

    if (abTestVariantFromQuery) {
        return abTestVariantFromQuery;
    }

    return abTestVariantFromCookie ?? abTestVariant;
};

/**
 * Parse cookies to find AB test variant
 * @param abTestId {string} id of the AB test
 * @param req {Request} request object
 * @returns {string | null} AB test variant
 */
export const getABTestVariantFromCookies = (abTestId: string, req?: Partial<Request>): string | null => {
    if (req && req.cookies) {
        return req.cookies[abTestId] ?? null;
    }

    return null;
};

/**
 * Resolve variant weights from config and validate against variant names
 * @param variantConfig {VariantConfig} variant configuration (object or function)
 * @param variants {string[]} variant names
 * @param params {IVariantConfigParams} parameters for function-based config
 * @returns {VariantWeights} resolved weights
 */
const resolveVariantWeights = (variantConfig: VariantConfig, variants: string[], params: IVariantConfigParams): VariantWeights => {
    // Resolve config - if function, call it; if object, use directly
    const weights: VariantWeights = typeof variantConfig === "function" ? variantConfig(params) : variantConfig;

    // Validate that all variant names have weights
    for (const variant of variants) {
        if (weights[variant] === undefined) {
            throw new Error(`variantConfig missing weight for variant "${variant}". Expected weights for all variants: ${variants.join(", ")}`);
        }
    }

    return weights;
};

/**
 * Validate variant weights
 * @param weights {VariantWeights} variant weights
 * @param variants {string[]} variant names
 */
const validateVariantWeights = (weights: VariantWeights, variants: string[]): void => {
    const weightValues = variants.map((v) => weights[v]);

    if (weightValues.length !== variants.length) {
        throw new Error(`variantConfig must have weights for all variants. Expected ${variants.length} variants, got ${weightValues.length}`);
    }

    if (weightValues.some((w) => w < 0 || w > 1)) {
        throw new Error("All weights must be between 0 and 1");
    }

    const sum = weightValues.reduce((acc, w) => acc + w, 0);
    if (Math.abs(sum - 1) > WEIGHT_SUM_TOLERANCE) {
        throw new Error(`Weights must sum to 1.0. Current sum: ${sum}`);
    }
};

/**
 * Draw new AB test variant.
 * @param variants {string[]} variant names
 * @param weights {VariantWeights} optional weights for weighted random selection
 * @returns {string} randomly selected variant
 */
const drawABTestVariant = (variants: string[], weights?: VariantWeights): string => {
    if (weights && Object.keys(weights).length === variants.length) {
        return getWeightedRandomVariant(weights);
    }
    return variants[Math.floor(Math.random() * variants.length)];
};

export type IAmplitudeVariantsMap = Record<string, {value: string; payload?: unknown}>;

/**
 * Resolve AB test variant from Amplitude Experiment.
 * Amplitude is the primary source of truth. The cookie is used as a cache and is updated when Amplitude's assignment changes.
 * Falls back to the cookie if Amplitude fails, and to variants[0] (control) if both are unavailable.
 * @param abTestConfig {IABTestConfiguration} AB test configuration object (must have useAmplitude: true)
 * @param amplitudeVariants {IAmplitudeVariantsMap} pre-fetched Amplitude variants
 * @param domain {string} cookie domain
 * @param req {Request} request object
 * @param meta {IRequestMeta} request meta object
 * @returns {string} variant name
 */
export const specifyAmplitudeABTestVariant = (
    {id, expires, variants}: IABTestConfiguration,
    amplitudeVariants: IAmplitudeVariantsMap,
    domain: string,
    req?: Partial<Request>,
    meta?: Partial<IRequestMeta>
): string => {
    // QA wants to always test base variant in automated tests
    const isE2eTesting = req?.headers?.["x-request-source"] === "e2e";

    if (isE2eTesting) {
        return variants[0];
    }

    // Allow query param override (highest priority)
    const abTestVariantFromQuery = getAbTestVariantFromQuery(id, variants, false, req);

    if (abTestVariantFromQuery != null) {
        if (meta) {
            const headers = new Headers({"set-cookie": buildABTestCookie(id, abTestVariantFromQuery, expires, domain)});

            meta.serverResponseHeaders?.appendHeaders(headers);
        }

        return abTestVariantFromQuery;
    }

    // Amplitude is the source of truth — check it before the cookie
    const amplitudeVariant = amplitudeVariants[id];
    const hasAmplitudeVariant = Boolean(amplitudeVariant?.value && variants.includes(amplitudeVariant.value));

    if (hasAmplitudeVariant) {
        const resolvedVariant = amplitudeVariant!.value;
        const abTestVariantFromCookie = getABTestVariantFromCookies(id, req);

        // Update cookie only when Amplitude's assignment differs from the current cookie
        if (meta && resolvedVariant !== abTestVariantFromCookie) {
            const headers = new Headers({"set-cookie": buildABTestCookie(id, resolvedVariant, expires, domain)});

            meta.serverResponseHeaders?.appendHeaders(headers);
        }

        return resolvedVariant;
    }

    // Amplitude did not return a valid variant — fall back to cookie for session continuity
    const abTestVariantFromCookie = getABTestVariantFromCookies(id, req);

    if (abTestVariantFromCookie) {
        return abTestVariantFromCookie;
    }

    // No Amplitude response and no cookie — return control without setting a cookie
    // so the next request retries Amplitude rather than locking in control permanently
    return variants[0];
};
